import React, { useState } from 'react';
import { useEntityStats, type EntityKey } from '../../context/EntityStatsContext';
import { useSyncTracking } from '../../context/SyncTrackingContext';
import { useLocationContext } from '../../context/LocationContext';
import { visibleEntityKeys } from '../../lib/crmLabels';
import { getIsAdmin, getUsername, hasFullPracticeAccess, logout } from '../../api/authApi';
import { HelpModal } from './HelpModal';

export type SidebarKey = 'dashboard' | EntityKey | 'tracking' | 'analytics' | 'settings' | 'user-management';

interface SidebarProps {
    active: SidebarKey;
    onNavigate: (key: SidebarKey) => void;
}

const NAV_ICONS: Record<EntityKey, React.ReactNode> = {
    appointments: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="4.5" width="18" height="16" rx="2.5" /><path d="M3 9h18M8 2.5v4M16 2.5v4" />
        </svg>
    ),
    providers: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="8" r="3.4" /><path d="M5.5 20a6.5 6.5 0 0 1 13 0" />
        </svg>
    ),
    status: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20.5 12 12.7 4.2A2 2 0 0 0 11.3 3.6H5a2 2 0 0 0-2 2v6.3a2 2 0 0 0 .6 1.4L11.4 21a1.4 1.4 0 0 0 2 0l7.1-7.1a1.4 1.4 0 0 0 0-1.9z" /><circle cx="7.5" cy="7.5" r="1.2" />
        </svg>
    ),
    services: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 3 3 8l9 5 9-5-9-5z" /><path d="M3 13l9 5 9-5" />
        </svg>
    ),
};

const NAV_LABELS: Record<EntityKey, string> = {
    appointments: 'Appointments',
    providers: 'Providers',
    status: 'Status',
    services: 'Services',
};

const NAV_ORDER: EntityKey[] = ['appointments', 'providers', 'status', 'services'];

export const Sidebar: React.FC<SidebarProps> = ({ active, onNavigate }) => {
    const { stats } = useEntityStats();
    const { attentionCount } = useSyncTracking();
    const { currentLocation } = useLocationContext();
    const navKeys = visibleEntityKeys(NAV_ORDER, currentLocation?.crm);
    const username = getUsername();
    const initials = username ? username.slice(0, 2).toUpperCase() : '--';
    const isAdmin = getIsAdmin();
    const canSeeTracking = hasFullPracticeAccess();
    const [helpOpen, setHelpOpen] = useState(false);

    const navItemClasses = (isActive: boolean) =>
        `flex items-center gap-[11px] px-[10px] py-[9px] rounded-[9px] text-sm font-medium text-left transition-colors ${
            isActive ? 'bg-[#EAF5F2] text-mapper-teal-hover font-semibold' : 'text-mapper-body-text'
        }`;

    return (
        <aside className="mapper-ui w-[250px] shrink-0 bg-mapper-white border-r border-mapper-border flex flex-col font-sans text-mapper-text">
            <div className="px-[22px] pt-[22px] pb-[18px] flex items-center gap-[11px]">
                <div className="w-[34px] h-[34px] rounded-[9px] bg-mapper-teal flex items-center justify-center shadow-mapper-logo">
                    <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="6" cy="6" r="2.4" /><circle cx="18" cy="18" r="2.4" />
                        <path d="M8.4 6H14a4 4 0 0 1 4 4v5.5" /><path d="M15.6 18H10a4 4 0 0 1-4-4V8.5" />
                    </svg>
                </div>
                <div className="leading-[1.1]">
                    <div className="font-extrabold text-base tracking-[-0.3px]">Advital Sync</div>
                    <div className="text-[11px] text-mapper-muted-2 font-medium">EMR ↔ Advital sync</div>
                </div>
            </div>

            <nav className="px-3 py-[6px] flex flex-col gap-[2px] flex-1 overflow-y-auto">
                <div className="text-[11px] font-bold tracking-[0.6px] text-mapper-muted-4 px-[10px] pt-3 pb-[6px]">OVERVIEW</div>
                <button onClick={() => onNavigate('dashboard')} className={navItemClasses(active === 'dashboard')}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="3" width="7" height="9" rx="1.5" /><rect x="14" y="3" width="7" height="5" rx="1.5" />
                        <rect x="14" y="12" width="7" height="9" rx="1.5" /><rect x="3" y="16" width="7" height="5" rx="1.5" />
                    </svg>
                    Dashboard
                </button>

                <div className="text-[11px] font-bold tracking-[0.6px] text-mapper-muted-4 px-[10px] pt-4 pb-[6px]">MAPPINGS</div>
                {navKeys.map(key => {
                    const count = stats[key]?.pending ?? 0;
                    return (
                        <button key={key} onClick={() => onNavigate(key)} className={navItemClasses(active === key)}>
                            {NAV_ICONS[key]}
                            <span className="flex-1">{NAV_LABELS[key]}</span>
                            {count > 0 && (
                                <span className="font-mono text-[11px] font-semibold bg-mapper-amber-bg text-mapper-amber px-[7px] py-[1px] rounded-full">
                                    {count}
                                </span>
                            )}
                        </button>
                    );
                })}

                <div className="text-[11px] font-bold tracking-[0.6px] text-mapper-muted-4 px-[10px] pt-4 pb-[6px]">MONITORING</div>
                {canSeeTracking && (
                    <button onClick={() => onNavigate('tracking')} className={navItemClasses(active === 'tracking')}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M22 12h-4l-3 8L9 4l-3 8H2" />
                        </svg>
                        <span className="flex-1">Sync Tracking</span>
                        {attentionCount > 0 && (
                            <span className="font-mono text-[11px] font-bold bg-mapper-red-bg text-mapper-red px-[7px] py-[1px] rounded-full">
                                {attentionCount}
                            </span>
                        )}
                    </button>
                )}
                <button onClick={() => onNavigate('analytics')} className={navItemClasses(active === 'analytics')}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M4 19V5" /><path d="M4 19h16" />
                        <rect x="7" y="11" width="3" height="5" rx="0.8" />
                        <rect x="12" y="7" width="3" height="9" rx="0.8" />
                        <rect x="17" y="9" width="3" height="7" rx="0.8" />
                    </svg>
                    <span className="flex-1">Sync Analytics</span>
                </button>

                <div className="text-[11px] font-bold tracking-[0.6px] text-mapper-muted-4 px-[10px] pt-4 pb-[6px]">SYSTEM</div>
                <button onClick={() => onNavigate('settings')} className={navItemClasses(active === 'settings')}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" />
                        <circle cx="12" cy="12" r="3" />
                    </svg>
                    Settings
                </button>
                {isAdmin && (
                    <button onClick={() => onNavigate('user-management')} className={navItemClasses(active === 'user-management')}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" />
                            <path d="M22 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
                        </svg>
                        User Management
                    </button>
                )}
            </nav>

            <div className="px-3 pt-2 pb-1 border-t border-mapper-border">
                <button
                    onClick={() => setHelpOpen(true)}
                    className="flex items-center gap-[11px] w-full px-[10px] py-[9px] rounded-[9px] text-sm font-medium text-left text-mapper-body-text"
                >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="9" />
                        <path d="M9.5 9a2.5 2.5 0 0 1 4.9.8c0 1.7-2.4 1.7-2.4 3.4" />
                        <path d="M12 17v.5" />
                    </svg>
                    Help
                </button>
            </div>

            <div className="px-4 py-[14px] flex items-center gap-[10px]">
                <div className="w-8 h-8 rounded-full bg-[#E8EDF2] flex items-center justify-center font-bold text-[13px] text-mapper-body-text">
                    {initials}
                </div>
                <div className="leading-[1.2] flex-1 min-w-0">
                    <div className="text-[13px] font-semibold truncate">{username ?? 'Signed in'}</div>
                    <div className="text-[11px] text-mapper-muted-2">Ad Vital</div>
                </div>
                <button
                    onClick={logout}
                    title="Log out"
                    aria-label="Log out"
                    className="w-8 h-8 rounded-lg shrink-0 text-mapper-muted-2 flex items-center justify-center hover:bg-mapper-border-4 hover:text-mapper-body-text"
                >
                    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                        <path d="M16 17l5-5-5-5" /><path d="M21 12H9" />
                    </svg>
                </button>
            </div>

            <HelpModal open={helpOpen} onClose={() => setHelpOpen(false)} />
        </aside>
    );
};
